import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/lib/i18n";

export default function SearchWidget() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [city, setCity] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [withDriver, setWithDriver] = useState("no");
  const [rentalType, setRentalType] = useState("day");

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    params.set("with_driver", withDriver === "yes" ? "true" : "false");
    if (rentalType) params.set("rental_type", rentalType);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <form
      onSubmit={submit}
      data-testid="search-widget"
      className="bg-white rounded-3xl mbk-card-shadow border border-gray-100 p-5 md:p-7 space-y-4"
    >
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">{t("home_search_destination")}</Label>
          <div className="relative mt-2">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#0e7a3f]" />
            <Input
              data-testid="search-city"
              className="pl-10 h-12 rounded-xl border-gray-200"
              placeholder={t("home_search_city_placeholder")}
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">{t("home_search_start")}</Label>
          <div className="relative mt-2">
            <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#0e7a3f]" />
            <Input data-testid="search-start" type="date" className="pl-10 h-12 rounded-xl border-gray-200" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">{t("home_search_end")}</Label>
          <div className="relative mt-2">
            <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#0e7a3f]" />
            <Input data-testid="search-end" type="date" className="pl-10 h-12 rounded-xl border-gray-200" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">{t("home_search_type")}</Label>
          <RadioGroup value={withDriver} onValueChange={setWithDriver} className="flex gap-4 mt-3">
            <div className="flex items-center gap-1.5">
              <RadioGroupItem data-testid="opt-no-driver" value="no" id="no-drv" />
              <Label htmlFor="no-drv" className="text-sm cursor-pointer">{t("home_search_no_driver")}</Label>
            </div>
            <div className="flex items-center gap-1.5">
              <RadioGroupItem data-testid="opt-with-driver" value="yes" id="with-drv" />
              <Label htmlFor="with-drv" className="text-sm cursor-pointer">{t("home_search_with_driver")}</Label>
            </div>
          </RadioGroup>
        </div>
      </div>
      <div className="flex flex-col md:flex-row md:items-end gap-4 pt-2">
        <div className="flex-1">
          <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">{t("home_search_rental_type")}</Label>
          <div className="flex gap-2 mt-2 flex-wrap">
            {[["day", t("rt_day")], ["week", t("rt_week")], ["month", t("rt_month")]].map(([k, l]) => (
              <button
                key={k}
                type="button"
                data-testid={`rt-${k}`}
                onClick={() => setRentalType(k)}
                className={`text-xs font-semibold rounded-full px-4 py-2 border transition ${rentalType === k ? "bg-[#0a1e3f] text-white border-[#0a1e3f]" : "bg-white text-gray-600 border-gray-200 hover:border-[#0a1e3f]"}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <Button
          data-testid="search-submit"
          type="submit"
          className="mbk-btn-primary h-12 px-8 gap-2 rounded-full text-white font-semibold w-full md:w-auto"
        >
          <Search className="h-4 w-4" /> {t("home_search_btn")}
        </Button>
      </div>
    </form>
  );
}
